import { Component, OnInit, effect, forwardRef, inject, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import {
    AbstractControl,
    ControlValueAccessor,
    FormArray,
    FormBuilder,
    FormGroup,
    NG_VALIDATORS,
    NG_VALUE_ACCESSOR,
    ReactiveFormsModule,
    ValidationErrors,
    Validator,
    Validators
} from '@angular/forms';
import { AngularMultiSelectModule } from 'angular2-multiselect-dropdown';
import { NgbPopoverModule } from '@ng-bootstrap/ng-bootstrap';
import { TranslateModule } from '@ngx-translate/core';
import { CdcService } from './cdc.service';
import { CDC_MULTIPLE_SETTINGS, CDC_SINGLE_SETTINGS } from './cdc.config';

export interface CdcItemOutput {
    enabled_disabled: boolean | string;
    value: string;
}

@Component({
    selector: 'app-cdc-multiselect',
    standalone: true,
    imports: [
        CommonModule,
        ReactiveFormsModule,
        AngularMultiSelectModule,
        NgbPopoverModule,
        TranslateModule
    ],
    templateUrl: './cdc-multiselect.component.html',
    styleUrls: ['../../richieste/area/area.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => CdcMultiselectComponent),
            multi: true
        },
        {
            provide: NG_VALIDATORS,
            useExisting: forwardRef(() => CdcMultiselectComponent),
            multi: true
        }
    ]
})
export class CdcMultiselectComponent implements OnInit, ControlValueAccessor, Validator {
    private readonly fb = inject(FormBuilder);
    private readonly moduleService = inject(CdcService);

    multiple = input<boolean>(true);
    multiRow = input<boolean>(true);

    cdcList: any[] = [];
    loading = false;
    cdcSetting: any;

    cdcFormArray: FormArray = this.fb.array([]);

    // Proprietà per tracciare se il FormControl padre ha il validatore 'required'
    private isRequired = false;

    constructor() {
        effect(() => {
            if (this.multiple()) {
                this.cdcSetting = CDC_MULTIPLE_SETTINGS;
            } else {
                this.cdcSetting = CDC_SINGLE_SETTINGS;
            }
        });
    }

    onChange: (value: CdcItemOutput[]) => void = () => { };
    onTouched: () => void = () => { };
    onValidatorChange: () => void = () => { };

    ngOnInit(): void {
        this.loadCdcList();

        if (this.cdcFormArray.length === 0) {
            this.addCDC();
        }
        
        this.cdcFormArray.valueChanges.subscribe(() => {
            this.onChange(this.getFormattedOutput());
            this.onTouched();
            this.onValidatorChange();
        });
    }

    get controls(): FormGroup[] {
        return this.cdcFormArray.controls as FormGroup[];
    }

    private initialDataToPopulate: any[] | null = null;
    loadCdcList(): void {
        this.loading = true;
        this.moduleService.getListOfCdc().subscribe({
            next: (response: any) => {
                this.cdcList = response.content;
                this.loading = false;

                if (this.initialDataToPopulate) {
                    this.populateFormArray(this.initialDataToPopulate);
                    this.initialDataToPopulate = null;
                }
            },
            error: (err) => {
                this.loading = false;
                console.error('Errore nel caricamento dei CDC:', err);
            }
        });
    }

    private populateFormArray(val: any[]): void {
        this.cdcFormArray.clear({ emitEvent: false });

        val.forEach(item => {
            let selectedObjects: any[] = [];

            if (typeof item.value === 'string' && item.value.trim() !== '') {
                const codes = item.value.split(',');
                selectedObjects = this.cdcList.filter(cdc => codes.includes(cdc.code));
            } else if (Array.isArray(item.value)) {
                selectedObjects = item.value;
            }

            // 1. Applichiamo 'required' solo se il parent lo richiede
            const valueValidators = this.isRequired ? [Validators.required, Validators.minLength(1)] : [];
            const enabledValidators = this.isRequired ? [Validators.required] : [];

            const group = this.fb.group({
                value: [selectedObjects, valueValidators],
                enabled_disabled: [item.enabled_disabled, enabledValidators]
            });

            this.cdcFormArray.push(group, { emitEvent: false });
        });

        this.onValidatorChange();
    }

    createGroup(): FormGroup {
        const valueValidators = this.isRequired ? [Validators.required, Validators.minLength(1)] : [];
        const enabledValidators = this.isRequired ? [Validators.required] : [];

        return this.fb.group({
            value: [[], valueValidators],
            enabled_disabled: [true, enabledValidators]
        });
    }

    addCDC(): void {
        this.cdcFormArray.push(this.createGroup());
    }

    removeCDC(index: number): void {
        if (this.cdcFormArray.length > 1) {
            this.cdcFormArray.removeAt(index);
        }
    }

    onCloseCdc(index: number): void {
        const control = this.controls[index]?.get('value');
        if (control && control.invalid) {
            control.markAsTouched();
        }
    }

    private getFormattedOutput(): CdcItemOutput[] {
        const result: CdcItemOutput[] = [];

        this.controls.forEach(group => {
            const value = group.get('value')?.value;
            const enabledDisabled = group.get('enabled_disabled')?.value;

            if (Array.isArray(value) && value.length > 0) {
                const codeString = value.map((v: any) => v.code).join(',');
                result.push({
                    enabled_disabled: enabledDisabled,
                    value: codeString
                });
            }
        });

        return result;
    }

    // Controlla se almeno uno dei controlli interni è stato toccato ed è invalido
    isFormArrayTouchedAndInvalid(): boolean {
        return this.controls.some(control => control.touched || control.dirty) && !!this.validate();
    }

    // Verifica se c'è un determinato tipo di errore (incorrect o nonabilitata)
    hasError(errorKey: string): boolean {
        const errors = this.validate();
        return errors ? !!errors[errorKey] : false;
    }

    // --- IMPLEMENTAZIONE VALIDATOR (Ripristino esatto della logica di TestCDC) ---

    validate(control?: AbstractControl): ValidationErrors | null {
        // 3. Rileviamo dinamicamente se il controllo padre ha il validatore 'required'
        if (control) {
            const hasRequiredValidator = control.validator ? control.validator({} as AbstractControl)?.['required'] : false;
            
            if (this.isRequired !== !!hasRequiredValidator) {
                this.isRequired = !!hasRequiredValidator;
                this.updateInternalValidators();
            }
        }

        // Se il campo NON è obbligatorio, non restituire errori di validazione
        if (!this.isRequired) {
            this.controls.forEach(group => {
                group.get('value')?.setErrors(null);
            });
            return null;
        }

        let isValidValue = true;
        let isValidEnabled = true;

        this.controls.forEach(group => {
            const valueCtrl = group.get('value');
            const enabledCtrl = group.get('enabled_disabled');

            if (!valueCtrl || !enabledCtrl) return;

            // Resettiamo gli errori precedenti sul singolo controllo
            valueCtrl.setErrors(null);

            const val = valueCtrl.value;
            const enabledVal = enabledCtrl.value;

            // 1. Verifica se il multiselect è vuoto
            if (!val || !Array.isArray(val) || val.length === 0) {
                isValidValue = false;
                valueCtrl.setErrors({ incorrect: true });
            }
            // 2. Verifica se il radio button non è stato selezionato (stringa vuota o null/undefined)
            else if (enabledVal === '' || enabledVal === null || enabledVal === undefined) {
                isValidEnabled = false;
                valueCtrl.setErrors({ nonabilitata: true });
            }
        });

        // Ritorna l'errore per il FormControl/FormArray nel Parent Component
        if (!isValidValue) {
            return { incorrect: true };
        }
        if (!isValidEnabled) {
            return { nonabilitata: true };
        }

        return null;
    }

    // Aggiorna dinamicamente i validatori dei controlli interni quando cambia lo stato di `isRequired`
    private updateInternalValidators(): void {
        const valueValidators = this.isRequired ? [Validators.required, Validators.minLength(1)] : [];
        const enabledValidators = this.isRequired ? [Validators.required] : [];

        this.controls.forEach(group => {
            const valCtrl = group.get('value');
            const enaCtrl = group.get('enabled_disabled');

            valCtrl?.setValidators(valueValidators);
            valCtrl?.updateValueAndValidity({ emitEvent: false });

            enaCtrl?.setValidators(enabledValidators);
            enaCtrl?.updateValueAndValidity({ emitEvent: false });
        });
    }

    registerOnValidatorChange(fn: () => void): void {
        this.onValidatorChange = fn;
    }

    // --- IMPLEMENTAZIONE CONTROL VALUE ACCESSOR ---

    writeValue(val: any[]): void {
        if (!val || !Array.isArray(val) || val.length === 0) {
            this.cdcFormArray.clear({ emitEvent: false });
            this.addCDC();
            return;
        }

        // Se la cdcList non è ancora pronta, salviamo i dati e li applichiamo al completamento di loadCdcList
        if (this.cdcList.length === 0) {
            this.initialDataToPopulate = val;
            return;
        }

        this.populateFormArray(val);
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouched = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        isDisabled ? this.cdcFormArray.disable() : this.cdcFormArray.enable();
    }
}